import { fetchApi } from './api';
import type {
    FrameOrder,
    FrameOrderDetail,
    FrameOrderFilterOptions,
    FrameOrderComment,
    FollowUpEntry,
    FrameOrderFilters,
    FrameOrderAggregateStats,
    CustomerExclude,
    CustomerSearchResult,
    SortField,
    SortOrder,
} from '../types/frameOrders';

interface PaginatedResponse<T> {
    items: T[];
    total: number;
    stats: FrameOrderAggregateStats;
}

interface ListParams {
    page: number;
    limit: number;
    sortBy: SortField;
    sortOrder: SortOrder;
    filters: FrameOrderFilters;
}

export const frameOrderApi = {
    async list(params: ListParams): Promise<PaginatedResponse<FrameOrder>> {
        const searchParams = new URLSearchParams();
        searchParams.set('page', String(params.page));
        searchParams.set('limit', String(params.limit));
        searchParams.set('sortBy', params.sortBy);
        searchParams.set('sortOrder', params.sortOrder);

        const { filters } = params;
        if (filters.companyCode) searchParams.set('companyCode', filters.companyCode);
        if (filters.documentType) searchParams.set('documentType', filters.documentType);
        if (filters.classificationCode) searchParams.set('classificationCode', filters.classificationCode);
        if (filters.classificationLabel) searchParams.set('classificationLabel', filters.classificationLabel);
        if (filters.salesCoordinator) searchParams.set('salesCoordinator', filters.salesCoordinator);
        if (filters.salesManager) searchParams.set('salesManager', filters.salesManager);
        if (filters.salesTeam) searchParams.set('salesTeam', filters.salesTeam);
        if (filters.search) searchParams.set('search', filters.search);
        if (filters.documentId) searchParams.set('documentId', filters.documentId);

        const response = await fetch(`/api/v1/frame-orders?${searchParams}`, {
            headers: { 'Content-Type': 'application/json' },
        });
        const body = await response.json();

        if (!response.ok || !body.success) {
            throw new Error(body.error?.message || `API error: ${response.status}`);
        }

        return {
            items: body.data as FrameOrder[],
            total: body.pagination?.total ?? 0,
            stats: body.stats ?? { totalOutstandingQty: 0, greenCount: 0, yellowCount: 0, redCount: 0, unknownCount: 0 },
        };
    },

    get(documentId: string): Promise<FrameOrderDetail> {
        return fetchApi<FrameOrderDetail>(`/frame-orders/${encodeURIComponent(documentId)}`);
    },

    getFilters(currentFilters?: FrameOrderFilters): Promise<FrameOrderFilterOptions> {
        const params = new URLSearchParams();
        if (currentFilters?.companyCode) params.set('companyCode', currentFilters.companyCode);
        if (currentFilters?.documentType) params.set('documentType', currentFilters.documentType);
        if (currentFilters?.classificationCode) params.set('classificationCode', currentFilters.classificationCode);
        if (currentFilters?.salesTeam) params.set('salesTeam', currentFilters.salesTeam);
        const qs = params.toString();
        return fetchApi<FrameOrderFilterOptions>(`/frame-orders/filters${qs ? `?${qs}` : ''}`);
    },

    addComment(documentId: string, commentText: string, commentType: string = 'note'): Promise<FrameOrderComment> {
        return fetchApi<FrameOrderComment>(`/frame-orders/${encodeURIComponent(documentId)}/comments`, {
            method: 'POST',
            body: JSON.stringify({ commentText, commentType }),
        });
    },

    followUpAction(
        documentId: string,
        action: 'create' | 'remove' | 'update',
        assignee?: string | null,
    ): Promise<{ removed?: boolean; updated?: boolean; id?: number; assignee?: string | null }> {
        return fetchApi(`/frame-orders/${encodeURIComponent(documentId)}/follow-up`, {
            method: 'POST',
            body: JSON.stringify({ action, assignee }),
        });
    },

    updateComment(documentId: string, commentId: number, commentText: string): Promise<unknown> {
        return fetchApi(`/frame-orders/${encodeURIComponent(documentId)}/comments/${commentId}`, {
            method: 'PUT',
            body: JSON.stringify({ commentText }),
        });
    },

    deleteComment(documentId: string, commentId: number): Promise<void> {
        return fetchApi<void>(`/frame-orders/${encodeURIComponent(documentId)}/comments/${commentId}`, {
            method: 'DELETE',
        });
    },

    getActiveEmployees(): Promise<string[]> {
        return fetchApi<string[]>('/frame-orders/employees');
    },

    getCustomerExcludes(): Promise<CustomerExclude[]> {
        return fetchApi<CustomerExclude[]>('/frame-orders/customer-excludes');
    },

    searchCustomers(companyCode: string, search: string): Promise<CustomerSearchResult[]> {
        const params = new URLSearchParams({ companyCode, search });
        return fetchApi<CustomerSearchResult[]>(`/frame-orders/customer-search?${params}`);
    },

    addCustomerExclude(payload: {
        customerId: string;
        companyCode: string;
        customerCode: string;
        customerName?: string | null;
        reason?: string | null;
    }): Promise<{ message: string; customerId: string }> {
        return fetchApi('/frame-orders/customer-excludes', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    },

    deleteCustomerExclude(customerId: string): Promise<void> {
        return fetchApi<void>(`/frame-orders/customer-excludes/${encodeURIComponent(customerId)}`, {
            method: 'DELETE',
        });
    },

    async getFollowUps(filters: FrameOrderFilters, assignee?: string): Promise<FollowUpEntry[]> {
        const searchParams = new URLSearchParams();
        if (filters.companyCode) searchParams.set('companyCode', filters.companyCode);
        if (filters.documentType) searchParams.set('documentType', filters.documentType);
        if (filters.classificationCode) searchParams.set('classificationCode', filters.classificationCode);
        if (filters.salesCoordinator) searchParams.set('salesCoordinator', filters.salesCoordinator);
        if (filters.salesManager) searchParams.set('salesManager', filters.salesManager);
        if (filters.salesTeam) searchParams.set('salesTeam', filters.salesTeam);
        if (filters.search) searchParams.set('search', filters.search);
        if (assignee) searchParams.set('assignee', assignee);
        const qs = searchParams.toString();
        return fetchApi<FollowUpEntry[]>(`/frame-order-follow-ups${qs ? `?${qs}` : ''}`);
    },
};
